import React, { useState } from 'react';
import { X } from 'lucide-react';
import { UserData } from '../types/user';
import { CostBreakdown } from '../types/renovation';
import { ContactSection } from './ContactSection';
import { Translations } from '../types/language';

interface Props {
  onSubmit: (data: UserData) => Promise<void>;
  onClose: () => void;
  translations: Translations;
  showResults?: boolean;
  breakdown?: CostBreakdown[];
  totalCost?: number;
  error?: string | null;
}

export function ContactForm({ 
  onSubmit, 
  onClose, 
  translations,
  showResults = false,
  breakdown = [],
  totalCost = 0,
  error
}: Props) {
  const [formData, setFormData] = useState<UserData>({
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      await onSubmit(formData);
    } catch (err) {
      console.error('Error submitting form:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="w-6 h-6" />
        </button>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}
        
        {showResults ? (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                {translations.title}
              </h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
                      {translations.service}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
                      {translations.area}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
                      {translations.cost}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {breakdown.map((item, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {translations.language === 'fr' ? item.categoryFr : item.category}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {item.details.area.toFixed(2)} m²
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {item.cost.toFixed(2)} €
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-blue-50">
                    <td colSpan={2} className="px-4 py-3 text-sm font-bold text-gray-900">
                      {translations.totalCost}
                    </td>
                    <td className="px-4 py-3 text-lg font-bold text-blue-600">
                      {totalCost.toFixed(2)} €
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="text-sm text-gray-500 border-t pt-4">
              <p className="font-medium">{translations.disclaimer.title}</p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>{translations.disclaimer.vatNote}</li>
                <li>{translations.disclaimer.varyNote}</li>
                <li>
                  {translations.disclaimer.consultNote}{' '}
                  <a 
                    href="https://abatiment.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {translations.disclaimer.amgLink}
                  </a>{' '}
                  {translations.language === 'fr' ? 'pour un devis détaillé' : 'for a detailed quote'}
                </li>
              </ul>
            </div>

            <ContactSection 
              translations={translations} 
              breakdown={breakdown}
              totalCost={totalCost}
              userData={formData}
            />
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {translations.contactForm.title}
            </h2>
            
            <p className="text-gray-600 mb-6">
              {translations.contactForm.description}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {translations.contactForm.email}
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="example@email.com"
                />
              </div>

              <p className="text-sm text-gray-500 italic">
                {translations.contactForm.gdprNotice}
              </p>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-3 px-4 bg-blue-600 text-white rounded-md transition-colors duration-200 ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
                }`}
              >
                {isSubmitting ? '...' : translations.contactForm.submit}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}